'use client';

import { m } from 'framer-motion';
import { ShieldCheck } from 'lucide-react';
import Link from 'next/link';

export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 py-16 px-4 sm:px-6 lg:px-8">
      <div className="max-w-4xl mx-auto">
        <m.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 bg-blue-100 rounded-full">
              <ShieldCheck className="h-8 w-8 text-orange-700" />
            </div>
          </div>
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Privacy Policy</h1>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Last updated: January 20, 2026
          </p>
        </m.div>

        <m.div
          className="bg-white rounded-2xl shadow-lg p-8 mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="prose prose-blue max-w-none">
            <h2 className="text-2xl font-semibold text-gray-900 mb-6">1. Information We Collect</h2>
            <p className="text-gray-700 mb-6">
              We collect information that you provide directly to us when you use our services, including:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Account information (name, email, profile details)</li>
              <li>Content you create or upload</li>
              <li>Usage data and analytics</li>
              <li>Device and connection information</li>
              <li>Cookies and similar tracking technologies</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">2. How We Use Your Information</h2>
            <p className="text-gray-700 mb-6">
              We use the information we collect to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Provide, maintain, and improve our services</li>
              <li>Develop new features and functionality</li>
              <li>Personalize your experience</li>
              <li>Communicate with you about updates and support</li>
              <li>Ensure security and prevent fraud</li>
              <li>Comply with legal obligations</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">3. Data Sharing and Disclosure</h2>
            <p className="text-gray-700 mb-6">
              We may share your information with:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Service providers who assist in operating our services</li>
              <li>Business partners for integrated features</li>
              <li>Legal authorities when required by law</li>
              <li>Affiliates and in connection with business transfers</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">4. Data Security</h2>
            <p className="text-gray-700 mb-6">
              We implement appropriate technical and organizational measures to protect your personal information. However, no method of transmission over the internet is 100% secure.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">5. Your Rights</h2>
            <p className="text-gray-700 mb-6">
              You have the right to:
            </p>
            <ul className="list-disc pl-6 mb-6 space-y-2 text-gray-700">
              <li>Access and receive a copy of your personal data</li>
              <li>Rectify or update your information</li>
              <li>Request deletion of your data</li>
              <li>Object to or restrict processing</li>
              <li>Withdraw consent where applicable</li>
            </ul>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">6. Changes to This Policy</h2>
            <p className="text-gray-700 mb-6">
              We may update this Privacy Policy from time to time. We'll notify you of any changes by posting the new Privacy Policy on this page and updating the "Last updated" date.
            </p>

            <h2 className="text-2xl font-semibold text-gray-900 mb-6 mt-10">7. Contact Us</h2>
            <p className="text-gray-700 mb-6">
              If you have any questions about this Privacy Policy, please contact us at:
            </p>
            <p className="text-gray-700">
              Email: support@axist.app
            </p>
          </div>
        </m.div>

        <div className="text-center text-gray-500 text-sm">
          <p>© {new Date().getFullYear()} Axist. All rights reserved. |
            <Link href="/terms" className="text-orange-700 hover:underline ml-1">Terms of Use</Link>
          </p>
        </div>
      </div>
    </div>
  );
}